/**
 * Cop-car steal — once-per-3-races invulnerability ultimate.
 *
 * Trigger: hold S + Shift simultaneously while within ~60 px of any cop
 * (NOT FBI — only the standard cop sedans count). On trigger:
 *   - server is asked to consume the cooldown via POST /cop_steal/use
 *   - the targeted cop is destroyed (out of the race)
 *   - player flips into "stolen-cop" mode: rendered as the cop sprite,
 *     invulnerable to cop / FBI / gang for the rest of THIS race.
 *
 * The previous car's body / accent colors are preserved so when the
 * player starts the next race, they're back in their own ride.
 */
const STEAL_RADIUS = 60;

/**
 * Per-frame helper. Returns the index of the closest valid cop within
 * STEAL_RADIUS, or -1 if none. Skips disabled / destroyed enemies.
 */
function _closestCopIndex(st) {
  const p = st.player;
  let best = -1;
  let bestD = STEAL_RADIUS;
  const now = performance.now();
  for (let i = 0; i < (st.enemies || []).length; i++) {
    const e = st.enemies[i];
    if (!e) continue;
    if (e.type !== "cop") continue;
    if (st.destroyedEnemyIdx?.has(i)) continue;
    if ((e.disabled || 0) > now) continue;
    const d = Math.hypot(e.x - p.x, e.y - p.y);
    if (d <= bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * Returns `true` if the trigger should fire on this frame.
 * Conditions: not already stolen, status currently "available", S + Shift
 * both held, near a cop. As a side-effect, snapshots the targeted cop's
 * index on `st._copStealTargetIdx` so `applyCopSteal` can use it later
 * (after the async server confirmation) even if the cop has since moved.
 */
export function shouldTriggerCopSteal(st, copStealStatus) {
  if (!st || !st.player) return false;
  if (st.player.copStolen) return false;
  if (!copStealStatus || !copStealStatus.available) return false;
  if (st.player.jailedUntil > performance.now()) return false;
  if (st.gang && st.gang.state === "stolen") return false;
  const keys = st.keys || {};
  // Both keys held simultaneously. "S" (or arrowdown) + "Shift".
  const sHeld = !!keys["s"] || !!keys["arrowdown"];
  const shiftHeld = !!keys["shift"];
  if (!(sHeld && shiftHeld)) return false;
  const idx = _closestCopIndex(st);
  if (idx < 0) return false;
  st._copStealTargetIdx = idx;
  return true;
}

/**
 * Apply the cop-car-steal transformation. Caller is expected to have
 * already gated on `shouldTriggerCopSteal` AND received a 200 from the
 * /cop_steal/use endpoint.
 */
export function applyCopSteal(st) {
  if (!st || !st.player || st.player.copStolen) return;
  // Use the cop index snapshotted at trigger time. The cop AI may have
  // moved since shouldTriggerCopSteal fired; we still steal that car.
  let idx = st._copStealTargetIdx ?? -1;
  if (idx < 0 || !st.enemies?.[idx] || st.destroyedEnemyIdx?.has(idx)) {
    idx = _closestCopIndex(st);
  }
  if (idx < 0) return;
  const cop = st.enemies[idx];
  // Disable the cop you stole the car from — they're now on foot.
  st.destroyedEnemyIdx?.add(idx);
  // Swap the player to the cop's position to look like the steal happened.
  // (Player's existing momentum is preserved.)
  st.player.x = cop.x;
  st.player.y = cop.y;
  st.player.copStolen = true;
  st.player.copStolenAt = performance.now();
  // Invulnerability flag — checked by enemy/jail/gang updaters.
  st.player.invulnerable = true;
  // Heal to full so a "weak HP at steal time" doesn't cause the player
  // to die anyway from a passive obstacle nick.
  st.player.hp = st.player.max_hp || 100;
  st.player.hitsTaken = 0;
  st._copStealTargetIdx = null;
}

/**
 * Render the player car as a cop cruiser (red + blue lightbar, white +
 * black two-tone) when copStolen is active. Drawn only when the flag is
 * set; otherwise no-op.
 */
export function drawStolenCopCar(ctx, p) {
  if (!p || !p.copStolen) return;
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle || 0);
  // Body — pixel-art cop cruiser, top-down.
  // Stripped down silhouette so it reads at the same scale as other cars.
  const W = 56, H = 30;
  // White roof / hood
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(-W / 2, -H / 2, W, H);
  // Black side stripe
  ctx.fillStyle = "#0e0c12";
  ctx.fillRect(-W / 2, -3, W, 6);
  // Black hood / trunk caps
  ctx.fillStyle = "#1a1a1f";
  ctx.fillRect(-W / 2, -H / 2, 6, H);
  ctx.fillRect(W / 2 - 6, -H / 2, 6, H);
  // Lightbar — red / blue alternating every 220ms
  const t = Math.floor(performance.now() / 220) % 2;
  ctx.fillStyle = t === 0 ? "#FF3030" : "#1E90FF";
  ctx.fillRect(-6, -H / 2 + 4, 6, 4);
  ctx.fillStyle = t === 0 ? "#1E90FF" : "#FF3030";
  ctx.fillRect(0, -H / 2 + 4, 6, 4);
  // Lightbar base
  ctx.fillStyle = "#0e0c12";
  ctx.fillRect(-7, -H / 2 + 3, 14, 1);
  ctx.fillRect(-7, -H / 2 + 8, 14, 1);
  // Windshield
  ctx.fillStyle = "#3a4d5c";
  ctx.fillRect(W / 2 - 18, -H / 2 + 4, 10, H - 8);
  // POLICE label on roof
  ctx.fillStyle = "#0e0c12";
  ctx.font = "bold 5px monospace";
  ctx.textAlign = "center";
  ctx.fillText("POLICE", 0, 2);
  // Wheels
  ctx.fillStyle = "#0e0c12";
  ctx.fillRect(-W / 2 + 6, -H / 2 - 3, 10, 3);
  ctx.fillRect(-W / 2 + 6, H / 2, 10, 3);
  ctx.fillRect(W / 2 - 16, -H / 2 - 3, 10, 3);
  ctx.fillRect(W / 2 - 16, H / 2, 10, 3);
  // Outline
  ctx.strokeStyle = "#0e0c12";
  ctx.lineWidth = 1;
  ctx.strokeRect(-W / 2, -H / 2, W, H);
  ctx.restore();
}

/**
 * "INVINCIBLE" pulsing badge above the player while the cop steal is
 * active. Subtle so it doesn't overshadow the rest of the HUD.
 */
export function drawInvincibleBadge(ctx, p) {
  if (!p || !p.copStolen) return;
  const t = (performance.now() - (p.copStolenAt || 0)) / 600;
  const a = 0.55 + 0.35 * Math.sin(t * Math.PI * 2);
  ctx.save();
  ctx.globalAlpha = a;
  ctx.font = "bold 9px 'Press Start 2P', monospace";
  ctx.textAlign = "center";
  ctx.fillStyle = "#FFD166";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 3;
  ctx.strokeText("INVINCIBLE", p.x, p.y - 50);
  ctx.fillText("INVINCIBLE", p.x, p.y - 50);
  ctx.restore();
}
